import { isInviteOnlySubscription } from "@/lib/admin-subscription-label";
import React, { useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  useAdminUsers,
  useUserSubscription,
  useUserLedger,
  useAssignPlan,
  useGrantCredits,
  useRevokeCredits,
  useRefundUser,
  useCreateUser,
  useImpersonateUser,
  useSuspendUser, useUserDeletionPreview, useUserDeletionJob, useDeleteUser, useRetryUserDeletion,
  type UserDeletionPreview, type UserDeletionJob,
} from "@/hooks/use-admin-users";
import { useAuthUser } from "@/App";
import { IMPERSONATION_QUERY } from "@/lib/impersonation";
import { useModelPlans } from "@/hooks/use-pricing-admin";

type ModalKind = "assign" | "grant" | "revoke" | "ledger" | "refund" | "delete" | null;

function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  const returnFocus = useRef(document.activeElement instanceof HTMLElement ? document.activeElement : null);
  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        aria-describedby={undefined}
        className="max-h-[85vh] w-[calc(100%_-_2rem)] max-w-xl overflow-auto rounded bg-white p-6 gap-0"
        onInteractOutside={(event) => event.preventDefault()}
        onCloseAutoFocus={(event) => {
          event.preventDefault();
          returnFocus.current?.focus();
        }}
      >
        <DialogTitle className="mb-4 pr-8 text-lg font-semibold">{title}</DialogTitle>
        {children}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Create user (v3.79) — admin-managed accounts for manual onboarding without
 * Stripe. One flow: create the account, optionally assign a plan (which grants
 * its monthly credits), optionally grant extra credits on top. Credits REQUIRE
 * an active plan (the ledger rejects grants without a subscription), so the
 * extra-credits field is disabled until a plan is picked.
 */
function CreateUserModal({ onClose }: { onClose: () => void }) {
  const plansQ = useModelPlans();
  const createUser = useCreateUser();
  const assign = useAssignPlan();
  const grant = useGrantCredits();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [planId, setPlanId] = useState<number | null>(null);
  const [extraCredits, setExtraCredits] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const plans = plansQ.data?.rows ?? [];
  const pickedPlan = plans.find((p: any) => p.id === planId);

  const valid =
    name.trim().length > 0 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    password.length >= 8 &&
    (extraCredits <= 0 || planId != null);

  async function doCreate() {
    if (!valid || working) return;
    setError(null);
    setWorking(true);
    try {
      const user = await createUser.mutateAsync({
        email: email.trim().toLowerCase(),
        password,
        name: name.trim(),
      });
      if (planId != null) {
        await assign.mutateAsync({ userId: user.id, planId });
      }
      if (extraCredits > 0) {
        await grant.mutateAsync({ userId: user.id, amount: extraCredits, reason: "Initial admin grant (account setup)" });
      }
      onClose();
    } catch (err) {
      const msg = (err as Error).message ?? "failed";
      setError(msg.includes("email_exists") || msg.includes("409") ? "An account with this email already exists." : `Failed: ${msg}`);
    } finally {
      setWorking(false);
    }
  }

  return (
    <Modal title="Create user" onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div>
          <label className="block text-xs font-medium text-[#526074]">Name</label>
          <input
            className="mt-1 w-full rounded border border-[#DCE3ED] px-3 py-2 text-sm"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
            data-testid="create-user-name"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#526074]">Email</label>
          <input
            type="email"
            className="mt-1 w-full rounded border border-[#DCE3ED] px-3 py-2 text-sm"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@company.com"
            data-testid="create-user-email"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#526074]">Password (min 8 characters)</label>
          <input
            type="text"
            className="mt-1 w-full rounded border border-[#DCE3ED] px-3 py-2 font-mono text-sm"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="they can change it later"
            data-testid="create-user-password"
          />
        </div>

        <div className="rounded border border-[#DCE3ED] bg-neutral-50 p-3 space-y-3">
          <div className="text-xs font-medium text-[#526074]">Optional — plan &amp; credits</div>
          <div>
            <label className="block text-xs font-medium text-[#526074]">Assign plan</label>
            <select
              className="mt-1 w-full rounded border border-[#DCE3ED] px-3 py-2 text-sm"
              value={planId ?? ""}
              onChange={(e) => setPlanId(Number(e.target.value) || null)}
              data-testid="create-user-plan"
            >
              <option value="">— no plan —</option>
              {plans.map((p: any) => (
                <option key={p.id} value={p.id}>
                  {p.name} — ${(p.priceCents / 100).toFixed(0)}/mo · {p.creditsPerMonth.toLocaleString()} credits
                </option>
              ))}
            </select>
            {pickedPlan && (
              <div className="mt-1 text-[11px] text-[#526074]">
                Grants {pickedPlan.creditsPerMonth.toLocaleString()} credits immediately.
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-[#526074]">Extra credits on top</label>
            <input
              type="number"
              min="0"
              className="mt-1 w-40 rounded border border-[#DCE3ED] px-3 py-2 font-mono text-sm disabled:opacity-40"
              value={extraCredits || ""}
              onChange={(e) => setExtraCredits(Math.max(0, Number(e.target.value)))}
              disabled={planId == null}
              data-testid="create-user-credits"
            />
            {planId == null && (
              <div className="mt-1 text-[11px] text-[#526074]">Credits require a plan — pick one above first.</div>
            )}
          </div>
        </div>

        {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">{error}</div>}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded border border-[#DCE3ED] bg-white px-3 py-1.5 text-sm" disabled={working}>Cancel</button>
          <button
            onClick={doCreate}
            disabled={!valid || working}
            className="rounded bg-black px-3 py-1.5 text-sm text-white disabled:opacity-40"
            data-testid="create-user-confirm"
          >
            {working ? "Creating…" : "Create user"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function AssignPlanModal({ userId, onClose }: { userId: number; onClose: () => void }) {
  const plansQ = useModelPlans();
  const sub = useUserSubscription(userId);
  const assign = useAssignPlan();
  const [planId, setPlanId] = useState<number | null>(null);

  const plans = plansQ.data?.rows ?? [];
  const current = sub.data?.subscription;
  const currentBalance = Number(current?.cachedBalance ?? 0);
  const pickedPlan = plans.find((p: any) => p.id === planId);

  async function doAssign() {
    if (!planId) return;
    await assign.mutateAsync({ userId, planId });
    onClose();
  }

  return (
    <Modal title="Assign plan" onClose={onClose}>
      <div className="space-y-4 text-sm">
        {current && (
          <div className="rounded bg-neutral-50 p-3">
            <div className="text-xs uppercase text-[#526074]">Current</div>
            <div>Plan ID: {current.planId} · Balance: {currentBalance.toLocaleString()} credits</div>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-[#526074]">Pick a plan</label>
          <select
            className="mt-1 w-full rounded border border-[#DCE3ED] px-3 py-2 text-sm"
            value={planId ?? ""}
            onChange={(e) => setPlanId(Number(e.target.value) || null)}
            data-testid="assign-plan-select"
          >
            <option value="">— choose —</option>
            {plans.map((p: any) => (
              <option key={p.id} value={p.id}>
                {p.name} — ${(p.priceCents / 100).toFixed(0)}/mo · {p.creditsPerMonth.toLocaleString()} credits
              </option>
            ))}
          </select>
        </div>

        {pickedPlan && (
          <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
            Assigning <b>{pickedPlan.name}</b> will cancel any active plan, create a new monthly cycle, and immediately grant {pickedPlan.creditsPerMonth.toLocaleString()} credits via the ledger.
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded border border-[#DCE3ED] bg-white px-3 py-1.5 text-sm">Cancel</button>
          <button
            onClick={doAssign}
            disabled={!planId || assign.isPending}
            className="rounded bg-black px-3 py-1.5 text-sm text-white disabled:opacity-40"
            data-testid="assign-plan-confirm"
          >
            {assign.isPending ? "Assigning…" : "Assign plan"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function GrantCreditsModal({ userId, onClose }: { userId: number; onClose: () => void }) {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState("");
  const grant = useGrantCredits();

  const canSubmit = amount > 0 && reason.trim().length >= 3 && !grant.isPending;

  async function doGrant() {
    await grant.mutateAsync({ userId, amount, reason: reason.trim() });
    onClose();
  }

  return (
    <Modal title="Grant credits" onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div>
          <label className="block text-xs font-medium text-[#526074]">Amount</label>
          <input
            type="number"
            min="1"
            className="mt-1 w-40 rounded border border-[#DCE3ED] px-3 py-2 font-mono text-sm"
            value={amount || ""}
            onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
            data-testid="grant-amount"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#526074]">Reason (≥3 chars)</label>
          <input
            className="mt-1 w-full rounded border border-[#DCE3ED] px-3 py-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. goodwill credit for support ticket #123"
            data-testid="grant-reason"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded border border-[#DCE3ED] bg-white px-3 py-1.5 text-sm">Cancel</button>
          <button
            onClick={doGrant}
            disabled={!canSubmit}
            className="rounded bg-black px-3 py-1.5 text-sm text-white disabled:opacity-40"
            data-testid="grant-confirm"
          >
            {grant.isPending ? "Granting…" : "Grant credits"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function RevokeCreditsModal({ userId, onClose }: { userId: number; onClose: () => void }) {
  const [amount, setAmount] = useState(0);
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const revoke = useRevokeCredits();

  const canSubmit = amount > 0 && reason.trim().length >= 3 && confirmed && !revoke.isPending;

  async function doRevoke() {
    await revoke.mutateAsync({ userId, amount, reason: reason.trim() });
    onClose();
  }

  return (
    <Modal title="Revoke credits" onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">
          Revoking credits reduces the user's balance immediately. They may hit "out of credits" on their next action.
        </div>
        <div>
          <label className="block text-xs font-medium text-[#526074]">Amount to revoke</label>
          <input
            type="number"
            min="1"
            className="mt-1 w-40 rounded border border-[#DCE3ED] px-3 py-2 font-mono text-sm"
            value={amount || ""}
            onChange={(e) => setAmount(Math.max(0, Number(e.target.value)))}
            data-testid="revoke-amount"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[#526074]">Reason (≥3 chars)</label>
          <input
            className="mt-1 w-full rounded border border-[#DCE3ED] px-3 py-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. corrected erroneous grant"
            data-testid="revoke-reason"
          />
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="revoke-confirm" />
          I understand this will reduce the user's balance.
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded border border-[#DCE3ED] bg-white px-3 py-1.5 text-sm">Cancel</button>
          <button
            onClick={doRevoke}
            disabled={!canSubmit}
            className="rounded bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
            data-testid="revoke-submit"
          >
            {revoke.isPending ? "Revoking…" : "Revoke"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function LedgerModal({ userId, onClose }: { userId: number; onClose: () => void }) {
  const ledger = useUserLedger(userId, 200);
  const rows = ledger.data?.rows ?? [];
  return (
    <Modal title="User ledger (last 200 entries)" onClose={onClose}>
      <div className="space-y-2 text-xs">
        {ledger.isLoading && <div className="text-[#526074]">Loading…</div>}
        {!ledger.isLoading && rows.length === 0 && <div className="text-[#526074]">No ledger entries yet.</div>}
        {rows.map((r: any) => (
          <div key={r.id} className="flex items-center justify-between rounded border border-[#DCE3ED] p-2">
            <div>
              <div className="font-medium text-neutral-800">{r.kind}</div>
              <div className="text-[11px] text-[#526074]">
                {r.source} {r.reason ? `· ${r.reason}` : ""} {r.adminUserId ? `· by admin ${r.adminUserId}` : ""}
              </div>
              <div className="text-[11px] text-[#526074]">{r.createdAt}</div>
            </div>
            <div className={`font-mono tabular-nums ${Number(r.amount) < 0 ? "text-red-600" : "text-emerald-600"}`}>
              {Number(r.amount) > 0 ? `+${Number(r.amount).toLocaleString()}` : Number(r.amount).toLocaleString()}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  );
}

function RefundModal({ userId, onClose }: { userId: number; onClose: () => void }) {
  const [reason, setReason] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const refund = useRefundUser();

  const canSubmit = reason.trim().length >= 3 && confirmed && !refund.isPending;

  async function doRefund() {
    await refund.mutateAsync({ userId, reason: reason.trim() });
    onClose();
  }

  return (
    <Modal title="Refund + revoke access" onClose={onClose}>
      <div className="space-y-4 text-sm">
        <div className="rounded border border-red-200 bg-red-50 p-3 text-xs text-red-900">
          Marks the user's active subscription as <b>refunded</b>, zeros their current balance via the ledger, and cancels the plan. Payment refund must be processed outside the app (CRM / NMI).
        </div>
        <div>
          <label className="block text-xs font-medium text-[#526074]">Reason (≥3 chars)</label>
          <input
            className="mt-1 w-full rounded border border-[#DCE3ED] px-3 py-2 text-sm"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. 14-day money-back guarantee"
            data-testid="refund-reason"
          />
        </div>
        <label className="flex items-center gap-2 text-xs">
          <input type="checkbox" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} data-testid="refund-confirm" />
          I've already initiated the payment refund in the CRM / payment system.
        </label>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="rounded border border-[#DCE3ED] bg-white px-3 py-1.5 text-sm">Cancel</button>
          <button
            onClick={doRefund}
            disabled={!canSubmit}
            className="rounded bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-40"
            data-testid="refund-submit"
          >
            {refund.isPending ? "Processing…" : "Refund & revoke"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export function canConfirmUserDeletion(args:{email:string;confirmEmail:string;self:boolean;blocked:boolean;busy:boolean;loaded:boolean;job:UserDeletionJob|null|undefined}):boolean {
  return args.loaded && !args.self && !args.blocked && !args.busy && !args.job && args.email.length>0 && args.confirmEmail===args.email;
}

export function UserDeletionContent({preview,job,confirmEmail,onConfirmEmail,onDelete,onRetry,self,busy,loaded,error,onRefresh}: {
  preview?:UserDeletionPreview;job?:UserDeletionJob|null;confirmEmail:string;onConfirmEmail:(value:string)=>void;
  onDelete:()=>void;onRetry:()=>void;self:boolean;busy:boolean;loaded:boolean;error?:string;onRefresh:()=>void;
}) {
  const canDelete=canConfirmUserDeletion({email:preview?.user.email??"",confirmEmail,self,blocked:preview?.blocked??true,busy,loaded,job});
  const failed=job?.status==='failed'||job?.status==='partial';
  return <div className="space-y-4 text-sm">
    {self && <p role="alert" className="rounded bg-amber-50 p-3 text-amber-900">You cannot suspend or delete your own account.</p>}
    {error && <div role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-red-900">{error} <button className="underline" onClick={onRefresh}>Refresh status</button></div>}
    {job ? <div className="space-y-3" aria-live="polite" data-testid="user-deletion-status">
      <p className="font-semibold">{job.status==='completed'?'Deletion completed':failed?'Deletion incomplete':job.status==='queued'?'Deletion queued':'Deleting account…'}</p>
      <p className="text-xs text-[#526074]">Job {job.id} · {job.status}{job.stage?` · ${job.stage}`:''}</p>
      {job.error && <p role="alert" className="rounded bg-red-50 p-3 text-red-900">{job.error}</p>}
      {failed && <><p>Some steps may already be complete. Retry resumes the deletion job; this account remains unavailable.</p><button disabled={self||busy} onClick={onRetry} className="rounded bg-red-700 px-3 py-2 text-white disabled:opacity-40" data-testid="user-deletion-retry">{busy?'Retrying…':'Retry deletion'}</button></>}
      {!failed && job.status!=='completed' && <p>You can close this dialog. Deletion continues on the server.</p>}
    </div> : <>
      {!loaded && !error && <p>Loading deletion preview…</p>}
      {preview && <>
        <p className="rounded border border-red-200 bg-red-50 p-3 text-red-900"><strong>This cannot be undone.</strong> Deletion permanently removes this account, its owned data, and files not shared with another account.</p>
        <p>Active billing subscriptions will be cancelled. Accounting records are retained in the deletion audit; payment-provider history is not erased.</p>
        <p className="font-medium break-all">{preview.user.email}</p>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 rounded border border-[#DCE3ED] p-3" data-testid="user-deletion-counts">
          {Object.entries(preview.counts).filter(([,count])=>count>0).map(([name,count])=><React.Fragment key={name}><dt className="break-words">{name.replace(/_/g,' ').replace(/([a-z])([A-Z])/g,'$1 $2')}</dt><dd className="text-right font-mono">{count.toLocaleString()}</dd></React.Fragment>)}
        </dl>
        {preview.warnings.length>0 && <ul className="list-disc space-y-1 rounded bg-amber-50 py-3 pl-7 pr-3 text-amber-900">{preview.warnings.map((warning,index)=><li key={index}>{warning}</li>)}</ul>}
        {preview.blocked && <p role="alert" className="font-medium text-red-700">Deletion is blocked. Resolve the issues above and <button className="underline" onClick={onRefresh}>refresh the preview</button>.</p>}
        <label className="block" htmlFor="delete-user-email">Type the exact email address to confirm:</label>
        <input id="delete-user-email" autoComplete="off" spellCheck={false} value={confirmEmail} onChange={event=>onConfirmEmail(event.target.value)} className="w-full rounded border border-[#DCE3ED] px-3 py-2" data-testid="user-deletion-email" disabled={self||busy}/>
        <div className="flex justify-end"><button disabled={!canDelete} onClick={onDelete} className="rounded bg-red-700 px-3 py-2 text-white disabled:opacity-40" data-testid="user-deletion-confirm">{busy?'Starting deletion…':'Permanently delete user'}</button></div>
      </>}
    </>}
  </div>;
}

function UserDeletionModal({userId,self,onClose}:{userId:number;self:boolean;onClose:()=>void}) {
  const preview=useUserDeletionPreview(userId);const status=useUserDeletionJob(userId);
  const deletion=useDeleteUser();const retry=useRetryUserDeletion();
  const [confirmEmail,setConfirmEmail]=useState('');const [actionError,setActionError]=useState<string>();
  const busy=deletion.isPending||retry.isPending;
  const refresh=()=>{setActionError(undefined);void status.refetch();void preview.refetch();};
  const start=async()=>{
    if(!canConfirmUserDeletion({email:preview.data?.user.email??'',confirmEmail,self,blocked:preview.data?.blocked??true,busy,loaded:preview.isSuccess&&status.isSuccess,job:status.data}))return;
    setActionError(undefined);
    try{await deletion.mutateAsync({userId,confirmEmail});}catch(error){setActionError(error instanceof Error?error.message:'Deletion could not start.');}
  };
  const retryJob=async()=>{if(self||busy)return;setActionError(undefined);try{await retry.mutateAsync(userId);}catch(error){setActionError(error instanceof Error?error.message:'Retry failed.');}};
  const error=actionError || (status.error?String(status.error):!status.data&&preview.error?String(preview.error):undefined);
  return <Modal title="Delete user" onClose={onClose}><UserDeletionContent preview={preview.data} job={status.data} confirmEmail={confirmEmail} onConfirmEmail={setConfirmEmail} onDelete={()=>void start()} onRetry={()=>void retryJob()} self={self} busy={busy} loaded={preview.isSuccess&&status.isSuccess} error={error} onRefresh={refresh}/></Modal>;
}

function UserRow({ user }: { user: any }) {
  const sub = useUserSubscription(user.id);
  const [modal, setModal] = useState<ModalKind>(null);
  const me = useAuthUser();
  const impersonate = useImpersonateUser();
  const suspension = useSuspendUser();
  const [accessError,setAccessError] = useState<string>();
  const isSelf = me?.id === user.id;
  const accessStatus = user.accessStatus ?? "active";
  const toggleSuspension = async () => {
    if (isSelf || accessStatus === "deleting" || suspension.isPending) return;
    setAccessError(undefined);
    try { await suspension.mutateAsync({userId:user.id,suspended:accessStatus!=="suspended"}); }
    catch(error) {setAccessError(error instanceof Error?error.message:"Access update failed.");}
  };
  const [loginAsError, setLoginAsError] = useState<string | null>(null);

  // v7.54 — "Login as": open the window synchronously (popup blockers only
  // allow window.open inside the click), then point it at the app with the
  // freshly minted token once the server responds. The admin tab keeps its
  // own login — the token lives in the NEW window's sessionStorage only.
  const loginAs = () => {
    setLoginAsError(null);
    const win = window.open("", "_blank");
    impersonate.mutate(user.id, {
      onSuccess: ({ token }) => {
        const url = new URL("/canvas", window.location.origin);
        url.searchParams.set(IMPERSONATION_QUERY, token);
        if (win) win.location.href = url.href;
        else window.open(url.href, "_blank");
      },
      onError: (err) => {
        win?.close();
        setLoginAsError(err instanceof Error ? err.message : "Login as failed");
      },
    });
  };
  const s = sub.data?.subscription;

  return (
    <>
      <tr className="border-t border-[#DCE3ED]">
        <td className="px-4 py-2 font-mono text-xs text-[#526074]">{user.id}</td>
        <td className="px-4 py-2 text-sm text-neutral-800">{user.email}</td>
        <td className="px-4 py-2 text-sm">{user.name ?? <span className="text-[#526074]">—</span>}</td>
        <td className="px-4 py-2 text-sm"><span className={`rounded px-2 py-0.5 text-xs ${accessStatus==="active"?"bg-emerald-50 text-emerald-700":"bg-amber-50 text-amber-900"}`} data-testid={`user-${user.id}-access-status`}>{accessStatus}</span></td>
        <td className="px-4 py-2 text-sm">
          {s ? (
            isInviteOnlySubscription(s) ? (
              <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">Invite Only · plan {s.planId}</span>
            ) : s.status === "trialing" ? (
              <span className="rounded bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                trial · plan {s.planId} · ends {new Date(s.renewsAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
              </span>
            ) : (
              <span className="rounded bg-neutral-100 px-2 py-0.5 text-xs">{s.status} · plan {s.planId}</span>
            )
          ) : (
            <span className="text-[#526074]">none</span>
          )}
        </td>
        <td className="px-4 py-2 text-right font-mono text-sm tabular-nums">
          {s ? Number(s.cachedBalance).toLocaleString() : "—"}
        </td>
        <td className="px-4 py-2">
          <div className="flex flex-wrap justify-end gap-1">
            <button
              onClick={loginAs}
              disabled={impersonate.isPending || isSelf || accessStatus !== "active"}
              title={me?.id === user.id ? "This is your own account" : "Open this user's account in a new window — your admin login stays put"}
              className="rounded border border-indigo-300 bg-white px-2 py-1 text-xs text-indigo-700 hover:bg-indigo-50 disabled:opacity-40"
              data-testid={`user-${user.id}-login-as`}
            >
              {impersonate.isPending ? "Opening…" : "Login as"}
            </button>
            <button onClick={() => setModal("assign")} className="rounded border border-[#DCE3ED] bg-white px-2 py-1 text-xs hover:bg-[#F1F5F9]" data-testid={`user-${user.id}-assign`}>Assign</button>
            <button onClick={() => setModal("grant")} className="rounded border border-[#DCE3ED] bg-white px-2 py-1 text-xs hover:bg-[#F1F5F9]" data-testid={`user-${user.id}-grant`}>Grant</button>
            <button onClick={() => setModal("revoke")} className="rounded border border-[#DCE3ED] bg-white px-2 py-1 text-xs hover:bg-[#F1F5F9]" data-testid={`user-${user.id}-revoke`}>Revoke</button>
            <button onClick={() => setModal("ledger")} className="rounded border border-[#DCE3ED] bg-white px-2 py-1 text-xs hover:bg-[#F1F5F9]" data-testid={`user-${user.id}-ledger`}>Ledger</button>
            <button onClick={() => setModal("refund")} className="rounded border border-red-300 bg-white px-2 py-1 text-xs text-red-600 hover:bg-red-50" data-testid={`user-${user.id}-refund`}>Refund</button>
            <button onClick={()=>void toggleSuspension()} disabled={isSelf||accessStatus==='deleting'||suspension.isPending} title={isSelf?'This is your own account':undefined} className="rounded border border-amber-300 bg-white px-2 py-1 text-xs text-amber-800 hover:bg-amber-50 disabled:opacity-40" data-testid={`user-${user.id}-suspension`}>{suspension.isPending?'Updating…':accessStatus==='suspended'?'Unsuspend':'Suspend'}</button>
            <button onClick={()=>setModal('delete')} disabled={isSelf} title={isSelf?'This is your own account':undefined} className="rounded border border-red-300 bg-white px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-40" data-testid={`user-${user.id}-delete`}>{accessStatus==='deleting'?'Deletion status':'Delete'}</button>
          </div>
          {accessError && <div role="alert" className="mt-1 text-right text-[11px] text-red-600">{accessError}</div>}
          {loginAsError && <div className="mt-1 text-right text-[11px] text-red-600">{loginAsError}</div>}
        </td>
      </tr>
      {modal === "assign" && <AssignPlanModal userId={user.id} onClose={() => setModal(null)} />}
      {modal === "grant" && <GrantCreditsModal userId={user.id} onClose={() => setModal(null)} />}
      {modal === "revoke" && <RevokeCreditsModal userId={user.id} onClose={() => setModal(null)} />}
      {modal === "ledger" && <LedgerModal userId={user.id} onClose={() => setModal(null)} />}
      {modal === "refund" && <RefundModal userId={user.id} onClose={() => setModal(null)} />}
      {modal === "delete" && <UserDeletionModal userId={user.id} self={isSelf} onClose={()=>setModal(null)}/>}
    </>
  );
}

export default function AdminUsersPage() {
  const users = useAdminUsers();
  const [createOpen, setCreateOpen] = useState(false);

  if (users.isLoading) return <div className="admin-state mx-auto w-full max-w-[1440px] text-sm text-[#526074]">Loading users…</div>;
  if (users.error) return <div className="admin-state mx-auto w-full max-w-[1440px] text-sm text-red-600">Failed to load users: {String(users.error)}</div>;

  const rows = users.data ?? [];

  return (
    <div className="mx-auto w-full max-w-[1440px]">
      <div className="mb-1 flex items-center justify-between">
        <h1 className="text-2xl font-semibold">User management</h1>
        <button
          onClick={() => setCreateOpen(true)}
          className="rounded bg-black px-3 py-1.5 text-sm text-white hover:bg-neutral-800"
          data-testid="create-user-open"
        >
          + Create user
        </button>
      </div>
      <p className="mb-6 text-sm text-[#526074]">Create accounts, assign plans, grant or revoke credits, view per-user ledger, and refund subscriptions.</p>
      {createOpen && <CreateUserModal onClose={() => setCreateOpen(false)} />}

      <section className="rounded border border-[#DCE3ED] bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-xs text-[#526074]">
              <tr>
                <th className="px-4 py-2 font-medium">ID</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Access</th>
                <th className="px-4 py-2 font-medium">Subscription</th>
                <th className="px-4 py-2 font-medium">Balance</th>
                <th className="px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((u: any) => <UserRow key={u.id} user={u} />)}
            </tbody>
          </table>
          {rows.length === 0 && <div className="p-6 text-center text-sm text-[#526074]">No users yet.</div>}
        </div>
      </section>
    </div>
  );
}
